import { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  Controls,
  MiniMap,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  useReactFlow,
  type Node,
  type Edge,
  type Connection,
  type NodeChange,
  type EdgeChange,
  type OnConnect,
  type NodeTypes,
} from '@xyflow/react';
import { Plus, LayoutGrid, CalendarDays, Edit3, Lock, Monitor } from 'lucide-react';
import type { TaskDoc, RunDoc, ExecutionDoc, RunDayDivider } from '@mct/shared';
import { normalizeCatalogOptions, readTaskAreaIds } from '@mct/shared';
import { useAuthState } from '../hooks/useAuth';
import {
  useAssignees,
  useProject,
  useTasks,
  useRuns,
  useExecutionsForRun,
  useBacklogCache,
} from '../hooks/useFirestoreData';
import { useUiStore } from '../store/uiStore';
import { RunSelector } from '../components/DagView/RunSelector';
import { ViewToggle } from '../components/ProjectWork/ViewToggle';
import { TaskNode, type TaskNodeData } from '../components/DagView/TaskNode';
import { TaskDetailPanel } from '../components/TaskDetail/TaskDetailPanel';
import { AssigneeChip } from '../components/common/AssigneeChip';
import { ExecutionKindChip } from '../components/common/ExecutionKindChip';
import {
  HighlightControls,
  type HighlightAssigneeOption,
} from '../components/common/HighlightControls';
import { StatusBadge } from '../components/StatusBadge';
import { buildLabelColorIndexMap } from '../lib/areaColor';
import { resolveAssigneeColorClasses } from '../lib/assigneeColor';
import {
  decodeHighlightFromParams,
  encodeHighlightToParams,
  isEmptyHighlight,
  isTaskHighlighted,
  UNASSIGNED_KEY,
  type HighlightState,
} from '../lib/highlight';
import { useViewportTier } from '../lib/useViewportTier';
import { Button } from '../components/ui/Button';
import { Dialog } from '../components/ui/Dialog';
import { Input, Textarea, Label } from '../components/ui/Input';
import { Spinner } from '../components/ui/Spinner';
import { getDb } from '../lib/firebase';
import {
  createTask,
  deleteTask,
  updateTask,
  updateTaskPosition,
} from '../lib/firestore';
import { recordTaskDelete, recordTaskEdit } from '../lib/undoStack';
import { computeDagLayout } from '../lib/dagLayout';
import { wouldCreateCycle, wouldCreateCycleByAddingEdge } from '../lib/cycle';
import { throttle, cn } from '../lib/utils';
import type { ExecutionType } from '@mct/shared';
import { DayDividerLines } from '../components/DagView/DayDividerLines';
import { DayDividerLabels } from '../components/DagView/DayDividerLabels';
import { DayDividerEditor } from '../components/DagView/DayDividerEditor';
import { SeedPhaseDialog } from '../components/DagView/SeedPhaseDialog';
import {
  canEditRunCanvas,
  isRunSeeded,
  newDividerId,
  resolveTaskPosition,
  seedRunCanvas,
  updateRunDayDividers,
  updateRunTaskPosition,
} from '../lib/runCanvas';

const NODE_TYPES: NodeTypes = { task: TaskNode };

const NODE_WIDTH = 240;
const NODE_HEIGHT = 110;

export function DagPage() {
  const { projectId } = useParams<{ projectId: string }>();
  return (
    <ReactFlowProvider>
      <DagCanvas projectId={projectId!} />
    </ReactFlowProvider>
  );
}

function DagCanvas({ projectId }: { projectId: string }) {
  // Phase 2.2 responsive: mobile (`< sm`) hides the ReactFlow canvas
  // entirely and shows a vertical task-list fallback instead — the bars
  // and edges are illegible at phone widths and the canvas's pan/zoom
  // gestures fight the page's vertical scroll.
  const tier = useViewportTier();
  const isMobile = tier === 'mobile';

  const { user } = useAuthState();
  const { data: project } = useProject(projectId);
  const { data: tasks, loading: tasksLoading } = useTasks(projectId);
  const { data: runs } = useRuns(projectId);
  const { data: backlogCache } = useBacklogCache(projectId);
  const { data: assignees } = useAssignees(projectId);

  // Highlight state shared with TaskListPage. Both pages decode the
  // same `?highlightArea=...&highlightAssignee=...` params so flipping
  // between リスト ↔ DAG via the ViewToggle preserves the user's
  // emphasis without re-clicking the chips.
  const [searchParams, setSearchParams] = useSearchParams();
  const highlight = useMemo(
    () => decodeHighlightFromParams(searchParams),
    [searchParams],
  );
  const highlightActive = !isEmptyHighlight(highlight);
  const updateHighlight = useCallback(
    (next: HighlightState) => {
      setSearchParams(encodeHighlightToParams(next, searchParams), {
        replace: true,
      });
    },
    [searchParams, setSearchParams],
  );

  const areaCatalog = useMemo(() => {
    const normalized = normalizeCatalogOptions(project?.wbsCatalog?.area);
    const byLabel = new Map<string, { label: string; colorIndex?: number }>();
    for (const opt of normalized) byLabel.set(opt.label, opt);
    return Array.from(byLabel.values());
  }, [project?.wbsCatalog?.area]);
  const areaColorIndices = useMemo(
    () =>
      buildLabelColorIndexMap(
        areaCatalog.map((o) => ({ id: o.label, label: o.label, colorIndex: o.colorIndex })),
      ),
    [areaCatalog],
  );
  const highlightAreaOptions = useMemo<string[]>(() => {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const o of areaCatalog) {
      if (!seen.has(o.label)) {
        seen.add(o.label);
        out.push(o.label);
      }
    }
    for (const t of tasks) {
      const imp = t as TaskDoc & { area?: string | null };
      for (const id of readTaskAreaIds(t)) {
        const label = id.trim();
        if (label && !seen.has(label)) {
          seen.add(label);
          out.push(label);
        }
      }
      const legacy = (imp.area ?? '').trim();
      if (legacy && !seen.has(legacy)) {
        seen.add(legacy);
        out.push(legacy);
      }
    }
    return out;
  }, [areaCatalog, tasks]);
  const highlightAssigneeOptions = useMemo<HighlightAssigneeOption[]>(() => {
    const seenIds = new Set<string>();
    const opts: HighlightAssigneeOption[] = [];
    for (const a of assignees) {
      seenIds.add(a.assigneeId);
      opts.push({
        value: a.assigneeId,
        label: a.name,
        colorIndex: a.colorIndex,
      });
    }
    let hasUnassigned = false;
    for (const t of tasks) {
      if (!t.assigneeId) {
        hasUnassigned = true;
      }
    }
    opts.sort((a, b) => a.label.localeCompare(b.label, 'ja'));
    if (hasUnassigned) {
      opts.push({ value: UNASSIGNED_KEY, label: '(未割当)' });
    }
    return opts;
  }, [assignees, tasks]);

  const selectedRunId = useUiStore(
    (s) => s.selectedRunIdByProject[projectId] ?? null,
  );
  const setSelectedRun = useUiStore((s) => s.setSelectedRun);
  const selectedTaskId = useUiStore((s) => s.selectedTaskId);
  const setSelectedTask = useUiStore((s) => s.setSelectedTask);

  // URL ↔ task selection sync. `?task=<taskId>` makes the detail-panel
  // state shareable: open the link, the receiver lands on the same
  // task with the panel popped open. URL writes use `replace` so the
  // browser Back button isn't flooded with intermediate selections.
  useEffect(() => {
    const fromUrl = searchParams.get('task');
    if (fromUrl && fromUrl !== selectedTaskId) {
      setSelectedTask(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (selectedTaskId) next.set('task', selectedTaskId);
        else next.delete('task');
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTaskId]);

  useEffect(() => {
    if (!selectedRunId && runs.length > 0) {
      const active = runs.find((r) => r.status === 'active') ?? runs[0]!;
      setSelectedRun(projectId, active.runId);
    }
  }, [selectedRunId, runs, projectId, setSelectedRun]);

  const selectedRun: RunDoc | null = useMemo(
    () => runs.find((r) => r.runId === selectedRunId) ?? null,
    [runs, selectedRunId],
  );

  const { data: executions } = useExecutionsForRun(
    projectId,
    selectedRunId,
  );

  const executionByTaskId = useMemo(() => {
    const m = new Map<string, ExecutionDoc>();
    for (const e of executions) m.set(e.taskId, e);
    return m;
  }, [executions]);

  const backlogCountByIssueKey = useMemo(() => {
    const s = new Set<string>();
    for (const b of backlogCache) s.add(b.issueKey);
    return s;
  }, [backlogCache]);

  // ReactFlow nodes is a mixed list of `task` and `dayDivider` types;
  // we keep them in one state so applyNodeChanges + drag handlers fire
  // uniformly. Persistence in `onNodesChange` looks up the node type
  // before deciding which Firestore path to write to.
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [creating, setCreating] = useState(false);
  const [cycleError, setCycleError] = useState<string | null>(null);
  // Task IDs currently marquee-/multi-selected on the canvas. Fed by ReactFlow's
  // onSelectionChange; used by the「選択中を自動調整」button to restrict the
  // dagre re-layout to just those nodes.
  const [selectedNodeIds, setSelectedNodeIds] = useState<string[]>([]);
  // Which day-divider's editor popover is open, plus the screen-space
  // anchor rect of the label chip we attach the popover to.
  const [editingDivider, setEditingDivider] = useState<{
    id: string;
    anchor: DOMRect;
  } | null>(null);
  // Latest nodes mirrored in a ref so the persistence loop in
  // onNodesChange can read node `type` without recomputing state.
  const nodesRef = useRef<Node[]>([]);
  // Transient banner that acknowledges the last dependency edit so users
  // immediately notice stray add/remove clicks and can spot accidents.
  const [edgeToast, setEdgeToast] = useState<string | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showEdgeToast = useCallback((msg: string) => {
    setEdgeToast(msg);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setEdgeToast(null), 2500);
  }, []);
  const userRef = useRef(user);
  userRef.current = user;

  // Throttled writer: branches on whether the active run has been
  // seeded. Seeded → writes to the run's per-phase override map;
  // otherwise → writes to the project-wide TaskDoc.position template.
  const throttledSavePos = useMemo(
    () =>
      throttle((pid: string, tid: string, uid: string, x: number, y: number) => {
        const run = selectedRunRef.current;
        if (run && isRunSeeded(run)) {
          void updateRunTaskPosition(getDb(), pid, run.runId, tid, { x, y });
        } else {
          void updateTaskPosition(getDb(), pid, tid, uid, { x, y });
        }
      }, 500),
    [],
  );

  const dayDividers: readonly RunDayDivider[] = useMemo(
    () => selectedRun?.canvasOverrides?.dayDividers ?? [],
    [selectedRun?.canvasOverrides?.dayDividers],
  );

  const canEditCanvas = canEditRunCanvas(selectedRun);
  const seeded = isRunSeeded(selectedRun);
  const [seedDialogOpen, setSeedDialogOpen] = useState(false);

  // Mirror selectedRun in a ref so the throttled position writer can
  // route to the right Firestore path (run override vs task template)
  // without re-creating the throttle on every render.
  const selectedRunRef = useRef(selectedRun);
  useEffect(() => {
    selectedRunRef.current = selectedRun;
  }, [selectedRun]);

  /**
   * Cmd/Ctrl+Z undo for accidental task moves. Each completed drag
   * pushes one entry (covering all nodes that moved together when the
   * user drags a multi-selection). We capture each node's pre-drag
   * position in `dragStartPositions` on dragStart and snapshot the
   * delta on dragStop. Cap the stack so a long session can't grow it
   * unbounded.
   */
  type UndoEntry = {
    items: Array<{ taskId: string; before: { x: number; y: number } }>;
  };
  const UNDO_STACK_MAX = 50;
  const undoStackRef = useRef<UndoEntry[]>([]);
  const dragStartPositions = useRef<Map<string, { x: number; y: number }>>(
    new Map(),
  );

  const onNodeDragStart = useCallback(
    (_e: React.MouseEvent, _node: Node, dragged: Node[]) => {
      const m = new Map<string, { x: number; y: number }>();
      for (const n of dragged) {
        if (n.type !== 'task') continue;
        m.set(n.id, { x: n.position.x, y: n.position.y });
      }
      dragStartPositions.current = m;
    },
    [],
  );

  const onNodeDragStop = useCallback(
    (_e: React.MouseEvent, _node: Node, dragged: Node[]) => {
      const items: UndoEntry['items'] = [];
      for (const n of dragged) {
        if (n.type !== 'task') continue;
        const before = dragStartPositions.current.get(n.id);
        if (!before) continue;
        if (
          Math.abs(before.x - n.position.x) < 0.5 &&
          Math.abs(before.y - n.position.y) < 0.5
        ) {
          continue;
        }
        items.push({ taskId: n.id, before });
      }
      if (items.length === 0) return;
      const stack = undoStackRef.current;
      stack.push({ items });
      if (stack.length > UNDO_STACK_MAX) stack.shift();
    },
    [],
  );

  // Cmd/Ctrl+Z restores the most recent drag's pre-positions both
  // locally (instant feedback) and in Firestore (via the right path
  // for the active phase). Bail when focus is in a text field so the
  // browser's native input undo still works.
  useEffect(() => {
    const isEditableTarget = (t: EventTarget | null): boolean => {
      if (!(t instanceof HTMLElement)) return false;
      if (t.isContentEditable) return true;
      const tag = t.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
    };
    const onKey = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey)) return;
      if (e.key !== 'z' && e.key !== 'Z') return;
      if (e.shiftKey) return; // leave redo for the future
      if (isEditableTarget(e.target)) return;
      const u = userRef.current;
      const run = selectedRunRef.current;
      const stack = undoStackRef.current;
      if (stack.length === 0) return;
      e.preventDefault();
      const last = stack.pop()!;
      const beforeById = new Map(last.items.map((it) => [it.taskId, it.before]));
      setNodes((nds) =>
        nds.map((n) =>
          beforeById.has(n.id) ? { ...n, position: beforeById.get(n.id)! } : n,
        ),
      );
      const seededNow = run ? isRunSeeded(run) : false;
      for (const it of last.items) {
        if (seededNow && run) {
          void updateRunTaskPosition(
            getDb(),
            projectId,
            run.runId,
            it.taskId,
            it.before,
          );
        } else if (u) {
          void updateTaskPosition(getDb(), projectId, it.taskId, u.uid, it.before);
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [projectId]);

  // Sync from Firestore tasks -> local nodes.
  useEffect(() => {
    if (tasks.length === 0 && dayDividers.length === 0) {
      setNodes([]);
      setEdges([]);
      return;
    }
    const assigneeById = new Map(assignees.map((a) => [a.assigneeId, a]));
    const resolveAssigneeDoc = (t: TaskDoc) => {
      const id = t.assigneeId ?? null;
      return id ? assigneeById.get(id) ?? null : null;
    };

    const zeroPos = tasks.every(
      (t) => t.position.x === 0 && t.position.y === 0,
    );
    const layout = zeroPos
      ? computeDagLayout(
          tasks.map((t) => ({ id: t.taskId, width: NODE_WIDTH, height: NODE_HEIGHT })),
          tasks.flatMap((t) =>
            t.dependsOn.map((p) => ({ source: p, target: t.taskId })),
          ),
          { direction: 'LR' },
        )
      : null;

    const taskNodes: Node<TaskNodeData>[] = tasks.map((t) => {
      const exec = executionByTaskId.get(t.taskId);
      const backlogCount = exec
        ? exec.backlogIssueKeys.filter((k) => backlogCountByIssueKey.has(k))
            .length
        : 0;
      const assigneeDoc = resolveAssigneeDoc(t);
      const imp = t as TaskDoc & { area?: string | null };
      // Primary area for the node label / color (single-value here).
      const resolvedAreaId = (readTaskAreaIds(t)[0] ?? imp.area ?? null) as
        | string
        | null;
      const matches = isTaskHighlighted(
        { areaId: resolvedAreaId, assigneeId: t.assigneeId ?? null },
        highlight,
      );
      // Pull the assignee colour out of the same palette helper the chip
      // uses so the ring matches the AssigneeChip the card already
      // renders. `bg-*-100` → `ring-*-300` keeps the saturation in
      // line with the pastel pill background.
      const assigneeClasses = assigneeDoc
        ? resolveAssigneeColorClasses(
            { colorIndex: assigneeDoc.colorIndex },
            assigneeDoc.name,
          )
        : 'bg-slate-100';
      const ringColor =
        assigneeClasses
          .split(' ')
          .find((cls) => cls.startsWith('bg-'))
          ?.replace(/^bg-/, 'ring-')
          .replace(/-100$/, '-300') ?? 'ring-slate-300';
      const data: TaskNodeData = {
        name: t.name,
        category: t.category,
        subcategory: t.subcategory ?? null,
        assignee: assigneeDoc
          ? { name: assigneeDoc.name, colorIndex: assigneeDoc.colorIndex }
          : null,
        executionType: t.executionType,
        status: exec?.status ?? 'not_started',
        completedAt: exec?.completedAt?.toDate?.() ?? null,
        backlogCount,
        cycleDetected: t.cycleDetected,
        dimmed: highlightActive && !matches,
        highlighted: highlightActive && matches,
        ringColorClass: ringColor,
      };
      // Resolve the task's effective position for the active phase.
      // Seeded phases pull from canvasOverrides; non-seeded phases
      // (and the bootstrap layout pass) fall back to t.position.
      const overridePos = resolveTaskPosition(t, selectedRun);
      const pos = layout?.[t.taskId] ?? overridePos;
      return {
        id: t.taskId,
        type: 'task',
        position: pos,
        data,
        draggable: canEditCanvas,
      };
    });
    // Day-divider lines are rendered separately via <DayDividerLines>
    // (a ViewportPortal overlay) — keeping them out of the React Flow
    // node graph avoids the per-node bounding-box clipping that limited
    // the line to the visible viewport height.
    setNodes(taskNodes);

    const nextEdges: Edge[] = tasks.flatMap((t) =>
      t.dependsOn.map((p) => ({
        id: `${p}->${t.taskId}`,
        source: p,
        target: t.taskId,
      })),
    );
    setEdges(nextEdges);

    // Persist auto layout once so future reloads are stable.
    if (layout && user) {
      for (const t of tasks) {
        const p = layout[t.taskId];
        if (p && (p.x !== t.position.x || p.y !== t.position.y)) {
          void updateTaskPosition(getDb(), projectId, t.taskId, user.uid, p);
        }
      }
    }
  }, [
    tasks,
    executionByTaskId,
    backlogCountByIssueKey,
    project,
    projectId,
    user,
    dayDividers,
    canEditCanvas,
    selectedRun,
    assignees,
    highlight,
    highlightActive,
  ]);

  // Mirror the latest nodes for the persistence loop in onNodesChange.
  useEffect(() => {
    nodesRef.current = nodes;
  }, [nodes]);

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      setNodes((nds) => applyNodeChanges(changes, nds));
      const u = userRef.current;
      if (!u) return;
      for (const c of changes) {
        if (c.type !== 'position' || !c.position || c.dragging) continue;
        const node = nodesRef.current.find((n) => n.id === c.id);
        // Tasks: persist via the project-wide template (until per-phase
        // overrides ship in PR #3, this matches existing behaviour).
        if (node?.type === 'task') {
          throttledSavePos(projectId, c.id, u.uid, c.position.x, c.position.y);
          continue;
        }
        // Dividers don't reach onNodesChange anymore — they live in a
        // ViewportPortal overlay (DayDividerLines) rather than the
        // ReactFlow node graph, with their own drag handlers.
      }
    },
    [projectId, throttledSavePos],
  );

  // Optimistic in-memory copy of the drag-in-progress X for each
  // divider, layered on top of the persisted dayDividers list so the
  // line follows the cursor smoothly while we coalesce the Firestore
  // write to drag-end.
  const [draftDividerX, setDraftDividerX] = useState<Record<string, number>>({});
  const renderedDividers: readonly RunDayDivider[] = useMemo(
    () =>
      dayDividers.map((d) =>
        d.id in draftDividerX ? { ...d, x: draftDividerX[d.id]! } : d,
      ),
    [dayDividers, draftDividerX],
  );

  const onDividerDragChange = useCallback(
    (id: string, nextX: number) => {
      setDraftDividerX((prev) => ({ ...prev, [id]: nextX }));
    },
    [],
  );

  const onDividerDragEnd = useCallback(
    async (id: string, finalX: number) => {
      setDraftDividerX((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      if (!selectedRunId || !canEditCanvas) return;
      const next = dayDividers.map((d) =>
        d.id === id ? { ...d, x: finalX } : d,
      );
      try {
        await updateRunDayDividers(getDb(), projectId, selectedRunId, next);
      } catch (err) {
        console.error('updateRunDayDividers (drag end) failed', err);
      }
    },
    [selectedRunId, canEditCanvas, dayDividers, projectId],
  );

  const reactFlow = useReactFlow();

  const addDayDivider = useCallback(async () => {
    if (!selectedRunId || !canEditCanvas) return;
    const STEP = 240;
    let baseX: number;
    if (dayDividers.length > 0) {
      // Lay subsequent dividers out left → right from the rightmost.
      baseX = Math.max(...dayDividers.map((d) => d.x)) + STEP;
    } else if (tasks.length > 0) {
      // First divider for this phase: anchor 80 px to the LEFT of
      // the leftmost task in the current resolved layout. Picking up
      // resolveTaskPosition keeps us aligned with whatever phase view
      // is currently rendered (seeded vs project-wide template).
      let minX = Infinity;
      for (const t of tasks) {
        const p = resolveTaskPosition(t, selectedRun);
        if (p.x < minX) minX = p.x;
      }
      baseX = (Number.isFinite(minX) ? minX : 0) - 80;
    } else {
      // Empty graph fallback — use the viewport centre.
      const rect = (
        document.querySelector('.react-flow__renderer') as HTMLElement | null
      )?.getBoundingClientRect();
      const screenX = rect ? rect.left + rect.width / 2 : window.innerWidth / 2;
      const screenY = rect ? rect.top + rect.height / 2 : window.innerHeight / 2;
      baseX = reactFlow.screenToFlowPosition({ x: screenX, y: screenY }).x;
    }
    const next: RunDayDivider[] = [
      ...dayDividers,
      {
        id: newDividerId(),
        x: baseX,
        label: `${dayDividers.length + 1}日目`,
        date: null,
      },
    ];
    try {
      await updateRunDayDividers(getDb(), projectId, selectedRunId, next);
    } catch (err) {
      console.error('addDayDivider failed', err);
    }
  }, [
    selectedRunId,
    canEditCanvas,
    dayDividers,
    projectId,
    reactFlow,
    tasks,
    selectedRun,
  ]);

  const saveDividerEdit = useCallback(
    async (next: RunDayDivider) => {
      if (!selectedRunId) return;
      const updated = dayDividers.map((d) => (d.id === next.id ? next : d));
      await updateRunDayDividers(getDb(), projectId, selectedRunId, updated);
    },
    [selectedRunId, dayDividers, projectId],
  );

  const deleteDivider = useCallback(
    async (id: string) => {
      if (!selectedRunId) return;
      const updated = dayDividers.filter((d) => d.id !== id);
      await updateRunDayDividers(getDb(), projectId, selectedRunId, updated);
    },
    [selectedRunId, dayDividers, projectId],
  );

  const editingDividerObj = useMemo(
    () => dayDividers.find((d) => d.id === editingDivider?.id) ?? null,
    [dayDividers, editingDivider?.id],
  );

  const onSeedConfirm = useCallback(async () => {
    if (!selectedRunId || !canEditCanvas || seeded) return;
    await seedRunCanvas(
      getDb(),
      projectId,
      selectedRunId,
      tasks,
      selectedRun?.canvasOverrides,
    );
    setSeedDialogOpen(false);
  }, [selectedRunId, canEditCanvas, seeded, projectId, tasks, selectedRun?.canvasOverrides]);

  /**
   * Re-run dagre over just the selected tasks and write their new positions
   * back. Edges whose endpoints both sit inside the selection are included;
   * edges that cross the selection boundary are ignored for layout (the
   * unselected nodes are left alone).
   *
   * Anchor: we translate the dagre output so that the top-left of the new
   * layout matches the top-left of the current selection. That way the
   * cluster of cards stays roughly where the user had it, just internally
   * untangled.
   */
  const autoLayoutSelection = useCallback(
    (ids: readonly string[]) => {
      const u = userRef.current;
      if (!u) return;
      if (ids.length < 2) return;
      const selectedSet = new Set(ids);
      const selected = tasks.filter((t) => selectedSet.has(t.taskId));
      if (selected.length < 2) return;

      const laid = computeDagLayout(
        selected.map((t) => ({ id: t.taskId, width: NODE_WIDTH, height: NODE_HEIGHT })),
        selected.flatMap((t) =>
          t.dependsOn
            .filter((p) => selectedSet.has(p))
            .map((p) => ({ source: p, target: t.taskId })),
        ),
        { direction: 'LR' },
      );

      // Find current and new bounding boxes so we can translate the cluster
      // onto its previous top-left corner.
      let curMinX = Infinity,
        curMinY = Infinity;
      for (const t of selected) {
        curMinX = Math.min(curMinX, t.position.x);
        curMinY = Math.min(curMinY, t.position.y);
      }
      let newMinX = Infinity,
        newMinY = Infinity;
      for (const id of selectedSet) {
        const p = laid[id];
        if (!p) continue;
        newMinX = Math.min(newMinX, p.x);
        newMinY = Math.min(newMinY, p.y);
      }
      const dx = curMinX - newMinX;
      const dy = curMinY - newMinY;

      let moved = 0;
      const nextById = new Map<string, { x: number; y: number }>();
      for (const t of selected) {
        const p = laid[t.taskId];
        if (!p) continue;
        const nx = p.x + dx;
        const ny = p.y + dy;
        const cur = resolveTaskPosition(t, selectedRun);
        if (Math.abs(nx - cur.x) < 1 && Math.abs(ny - cur.y) < 1) continue;
        nextById.set(t.taskId, { x: nx, y: ny });
        if (seeded && selectedRun) {
          void updateRunTaskPosition(
            getDb(),
            projectId,
            selectedRun.runId,
            t.taskId,
            { x: nx, y: ny },
          );
        } else {
          void updateTaskPosition(getDb(), projectId, t.taskId, u.uid, {
            x: nx,
            y: ny,
          });
        }
        moved++;
      }
      // Update local node state so the user sees the new layout immediately
      // without waiting for the Firestore round-trip.
      if (nextById.size > 0) {
        setNodes((nds) =>
          nds.map((n) =>
            nextById.has(n.id) ? { ...n, position: nextById.get(n.id)! } : n,
          ),
        );
      }
      showEdgeToast(`選択中 ${selected.length} 件のうち ${moved} 件を再配置`);
    },
    [tasks, projectId, showEdgeToast, selectedRun, seeded],
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      setEdges((eds) => applyEdgeChanges(changes, eds));
      const u = userRef.current;
      if (!u) return;
      const removed: Array<{ source: string; target: string }> = [];
      for (const c of changes) {
        if (c.type === 'remove') {
          const idx = c.id.indexOf('->');
          if (idx < 0) continue;
          const source = c.id.slice(0, idx);
          const target = c.id.slice(idx + 2);
          if (!source || !target) continue;
          const child = tasks.find((t) => t.taskId === target);
          if (!child) continue;
          const nextDeps = child.dependsOn.filter((d) => d !== source);
          removed.push({ source, target });
          void updateTask(getDb(), projectId, target, u.uid, {
            dependsOn: nextDeps,
          });
        }
      }
      if (removed.length > 0) {
        const sample = removed[0]!;
        const more = removed.length > 1 ? ` (+${removed.length - 1})` : '';
        showEdgeToast(`依存を削除: ${sample.source} → ${sample.target}${more}`);
      }
    },
    [projectId, tasks, showEdgeToast],
  );

  const onConnect: OnConnect = useCallback(
    (connection: Connection) => {
      const u = userRef.current;
      if (!u || !connection.source || !connection.target) return;
      if (
        wouldCreateCycleByAddingEdge(
          tasks.map((t) => ({
            taskId: t.taskId,
            dependsOn: t.dependsOn,
          })),
          connection.target,
          connection.source,
        )
      ) {
        setCycleError(
          '循環依存を作成することはできません。別のタスクから接続してください。',
        );
        return;
      }
      setEdges((eds) => addEdge(connection, eds));
      const child = tasks.find((t) => t.taskId === connection.target);
      if (!child) return;
      if (child.dependsOn.includes(connection.source)) {
        showEdgeToast(`依存は既に存在: ${connection.source} → ${connection.target}`);
        return;
      }
      const next = [...new Set([...child.dependsOn, connection.source])];
      void updateTask(getDb(), projectId, connection.target, u.uid, {
        dependsOn: next,
      });
      showEdgeToast(`依存を追加: ${connection.source} → ${connection.target}`);
    },
    [projectId, tasks, showEdgeToast],
  );

  const onNodeClick = useCallback(
    (_e: React.MouseEvent, node: Node) => {
      // Day-divider nodes are non-selectable (so they don't pollute
      // multi-select), but ReactFlow still surfaces their click —
      // ignore so the side panel doesn't try to load a missing task.
      if (node.type !== 'task') return;
      setSelectedTask(node.id);
    },
    [setSelectedTask],
  );

  /**
   * Stable callback for ReactFlow's selection listener. Stable
   * identity matters: ReactFlow's internal `SelectionListener`
   * subscribes to the prop and re-fires when it changes — combined
   * with `setSelectedNodeIds` always allocating a fresh array, an
   * inline arrow here causes a render → selection listener → setState
   * → render loop. We also bail early when the selection set is
   * functionally unchanged so identical clicks don't re-allocate.
   */
  const onSelectionChange = useCallback(
    ({ nodes: sel }: { nodes: Node[] }) => {
      const next = sel
        .filter((n) => n.type !== 'dayDivider')
        .map((n) => n.id);
      setSelectedNodeIds((prev) => {
        if (prev.length === next.length && prev.every((id, i) => id === next[i])) {
          return prev;
        }
        return next;
      });
    },
    [],
  );

  // Called by TaskDetailPanel when the user edits dependencies there. Only
  // writes the new dependsOn to Firestore — node positions are preserved so
  // the user can decide when (and for which cards) to re-layout via the
  // canvas toolbar.
  const onUpdateDependsOn = useCallback(
    async (taskId: string, nextDependsOn: string[]): Promise<'ok' | 'cycle' | 'error'> => {
      const u = userRef.current;
      if (!u) return 'error';
      const current = tasks.find((t) => t.taskId === taskId);
      if (!current) return 'error';
      const before = current.dependsOn;
      const added = nextDependsOn.filter((d) => !before.includes(d));
      const removed = before.filter((d) => !nextDependsOn.includes(d));
      if (added.length === 0 && removed.length === 0) return 'ok';
      const hasCycle = wouldCreateCycle(
        tasks.map((t) => ({ taskId: t.taskId, dependsOn: t.dependsOn })),
        taskId,
        nextDependsOn,
      );
      if (hasCycle) {
        setCycleError('循環依存を作成することはできません。');
        return 'cycle';
      }
      try {
        await updateTask(getDb(), projectId, taskId, u.uid, { dependsOn: nextDependsOn });
        recordTaskEdit({
          projectId,
          taskId,
          uid: u.uid,
          before: current,
          patch: { dependsOn: nextDependsOn },
          label: `"${current.name}" の依存`,
        });
        if (added.length > 0) {
          showEdgeToast(`依存を追加: ${added[0]} → ${taskId}${added.length > 1 ? ` (+${added.length - 1})` : ''}`);
        } else if (removed.length > 0) {
          showEdgeToast(`依存を削除: ${removed[0]} → ${taskId}${removed.length > 1 ? ` (+${removed.length - 1})` : ''}`);
        }
        return 'ok';
      } catch (err) {
        console.error('updateDependsOn failed', err);
        return 'error';
      }
    },
    [projectId, tasks, showEdgeToast],
  );

  const selectedTask = useMemo(
    () => tasks.find((t) => t.taskId === selectedTaskId) ?? null,
    [tasks, selectedTaskId],
  );

  return (
    <div className="relative flex h-full">
      <div
        className={cn(
          'relative flex-1',
          // Mobile uses an in-flow toolbar + scrollable fallback list,
          // so we need a flex column to size them. Desktop keeps the
          // legacy "toolbar floats over the canvas" layering.
          isMobile ? 'flex min-h-0 flex-col' : '',
        )}
      >
        <div
          data-testid="dag-toolbar"
          className={cn(
            'left-2 right-2 z-10 flex flex-wrap items-center justify-between gap-2 rounded-md bg-white/90 p-2 shadow-sm sm:left-4 sm:right-4 sm:gap-3',
            // On mobile we render the toolbar in flow above the
            // fallback list; on tablet+ it floats over the canvas as
            // before. `sm:absolute` re-establishes the original layout.
            isMobile ? 'relative top-0' : 'absolute top-3',
          )}
        >
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <ViewToggle />
            <RunSelector
              runs={runs}
              selectedRunId={selectedRunId}
              onChange={(id) => setSelectedRun(projectId, id)}
            />
            {/*
             * Highlight controls — same component the list view uses so
             * the chip-rail UX is identical between the two surfaces.
             * URL-state means flipping ViewToggle preserves emphasis.
             */}
            <HighlightControls
              state={highlight}
              onChange={updateHighlight}
              areaOptions={highlightAreaOptions}
              areaColorIndices={areaColorIndices}
              assigneeOptions={highlightAssigneeOptions}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {selectedRun && !selectedRun.executionsReady ? (
              <span className="hidden text-xs text-slate-500 sm:inline">
                Execution 生成中… {selectedRun.executionsGeneratedCount}/
                {selectedRun.expectedExecutionsCount}
              </span>
            ) : null}
            {selectedRun && selectedRun.status === 'completed' ? (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600"
                title="完了済みフェーズの配置は閲覧のみです"
              >
                <Lock className="h-3 w-3" />
                閲覧のみ
              </span>
            ) : seeded ? (
              <span
                className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-emerald-200"
                title="このフェーズは独自の配置を持っています"
              >
                <Edit3 className="h-3 w-3" />
                編集モード
              </span>
            ) : selectedRun && canEditCanvas ? (
              <Button
                variant="secondary"
                onClick={() => setSeedDialogOpen(true)}
                title="このフェーズを独自配置に切り替える"
              >
                <Edit3 className="mr-1 h-4 w-4" />
                このフェーズの編集を開始
              </Button>
            ) : null}
            {/* Canvas-only actions: 選択中を自動調整 / 区切り線を追加
                only make sense in the ReactFlow canvas, so we hide them
                on mobile where the canvas itself isn't rendered. */}
            <div className="hidden items-center gap-2 sm:flex">
              <Button
                variant="secondary"
                disabled={selectedNodeIds.length < 2}
                onClick={() => autoLayoutSelection(selectedNodeIds)}
                title={
                  selectedNodeIds.length < 2
                    ? '2つ以上のカードを選択してください (Shift+ドラッグで矩形選択 / Cmd+クリックで追加)'
                    : `選択中の ${selectedNodeIds.length} 件を整列`
                }
              >
                <LayoutGrid className="mr-1 h-4 w-4" />
                選択中を自動調整
                {selectedNodeIds.length >= 2 ? ` (${selectedNodeIds.length})` : ''}
              </Button>
              <Button
                variant="secondary"
                disabled={!canEditCanvas || !selectedRunId}
                onClick={addDayDivider}
                title={
                  !canEditCanvas
                    ? '完了済みフェーズでは編集できません'
                    : '現在のフェーズに日区切り線を追加'
                }
              >
                <CalendarDays className="mr-1 h-4 w-4" />
                区切り線を追加
              </Button>
            </div>
            <Button onClick={() => setCreating(true)}>
              <Plus className="mr-1 h-4 w-4" /> タスク
            </Button>
          </div>
        </div>

        {tasksLoading ? (
          <div className="flex h-full items-center justify-center">
            <Spinner />
          </div>
        ) : isMobile ? (
          <DagMobileFallback
            tasks={tasks}
            executionByTaskId={executionByTaskId}
            assignees={assignees}
            backlogKeys={backlogCountByIssueKey}
            onSelectTask={(id) => setSelectedTask(id)}
          />
        ) : (
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={NODE_TYPES}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onSelectionChange={onSelectionChange}
            onNodeDragStart={onNodeDragStart}
            onNodeDragStop={onNodeDragStop}
            fitView
            // Pan with wheel/trackpad scroll (horizontal → pan X, vertical →
            // pan Y). Cmd/Ctrl + wheel zooms, keeping the native-feeling gesture.
            panOnScroll
            panOnScrollSpeed={0.8}
            zoomOnScroll={false}
            zoomOnPinch
            // Allow zooming further out than ReactFlow's 0.5 default so a
            // 200+ task graph can be surveyed at a glance from the canvas
            // controls' minus button. Upper bound left at the default 2x.
            minZoom={0.05}
            maxZoom={2}
            // Shift + drag on empty space → marquee selection. Without this
            // users have no way to select >1 card short of Cmd-clicking each.
            selectionOnDrag={false}
            selectionKeyCode="Shift"
            multiSelectionKeyCode={['Meta', 'Control']}
            proOptions={{ hideAttribution: true }}
          >
            <Background gap={16} />
            <Controls />
            <MiniMap pannable />
            <DayDividerLines
              dividers={renderedDividers}
              canEdit={canEditCanvas}
              onDragChange={onDividerDragChange}
              onDragEnd={onDividerDragEnd}
            />
            <DayDividerLabels
              dividers={renderedDividers}
              readOnly={!canEditCanvas}
              onEdit={(id, anchor) => setEditingDivider({ id, anchor })}
            />
          </ReactFlow>
        )}

        {editingDivider && editingDividerObj ? (
          <DayDividerEditor
            divider={editingDividerObj}
            anchor={editingDivider.anchor}
            onSave={saveDividerEdit}
            onDelete={() => deleteDivider(editingDivider.id)}
            onClose={() => setEditingDivider(null)}
          />
        ) : null}

        {cycleError ? (
          <div className="absolute bottom-4 left-1/2 z-10 -translate-x-1/2 rounded-md bg-red-600 px-4 py-2 text-sm text-white shadow-lg">
            {cycleError}{' '}
            <button className="ml-2 underline" onClick={() => setCycleError(null)}>
              閉じる
            </button>
          </div>
        ) : null}
        {edgeToast ? (
          <div className="absolute bottom-4 left-1/2 z-10 -translate-x-1/2 rounded-md bg-slate-800 px-4 py-2 text-sm text-white shadow-lg">
            {edgeToast}
          </div>
        ) : null}
      </div>

      {/*
       * Overlay layout: the detail panel is taken out of the main flex
       * row and floated as an absolute layer instead. Without this,
       * opening the panel shrinks the canvas's flex-1 column from the
       * right, which in turn squishes the floating toolbar (フロー /
       * リスト / フェーズ etc.) into a narrow column. As an overlay it
       * sits on top of the canvas without affecting toolbar width.
       */}
      {selectedTask && project ? (
        <div className="pointer-events-none absolute inset-0 z-30 flex justify-end">
          <div className="pointer-events-auto h-full shadow-2xl">
            <TaskDetailPanel
              project={project}
              task={selectedTask}
              allTasks={tasks}
              currentRun={selectedRun}
              onClose={() => setSelectedTask(null)}
              onSelectTask={(id) => setSelectedTask(id)}
              onUpdateDependsOn={onUpdateDependsOn}
              onDelete={async () => {
                if (!user) return;
                if (!window.confirm('このタスクを削除しますか？関連する実行レコードも削除されます。')) return;
                const snapshot = selectedTask;
                await deleteTask(getDb(), projectId, snapshot.taskId);
                recordTaskDelete({
                  task: snapshot,
                  label: `"${snapshot.name}" の削除`,
                });
                setSelectedTask(null);
              }}
            />
          </div>
        </div>
      ) : null}

      <CreateTaskDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreate={async (input) => {
          if (!project || !user) return;
          await createTask(getDb(), project, user.uid, input);
          setCreating(false);
        }}
        tasks={tasks}
      />

      {selectedRun ? (
        <SeedPhaseDialog
          open={seedDialogOpen}
          phaseName={selectedRun.name}
          taskCount={tasks.length}
          onConfirm={onSeedConfirm}
          onClose={() => setSeedDialogOpen(false)}
        />
      ) : null}
    </div>
  );
}

function CreateTaskDialog({
  open,
  onClose,
  onCreate,
  tasks,
}: {
  open: boolean;
  onClose: () => void;
  onCreate: (input: {
    name: string;
    description?: string;
    category?: string;
    executionType: ExecutionType;
    estimatedMinutes: number;
    dependsOn: string[];
  }) => Promise<void>;
  tasks: readonly TaskDoc[];
}) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [category, setCategory] = useState('');
  const [type, setType] = useState<ExecutionType>('manual');
  const [mins, setMins] = useState(60);
  const [deps, setDeps] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="タスクを追加"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            キャンセル
          </Button>
          <Button
            disabled={busy || !name.trim()}
            onClick={async () => {
              setBusy(true);
              try {
                await onCreate({
                  name,
                  description: desc,
                  category,
                  executionType: type,
                  estimatedMinutes: mins,
                  dependsOn: deps,
                });
                setName('');
                setDesc('');
                setCategory('');
                setMins(60);
                setDeps([]);
              } finally {
                setBusy(false);
              }
            }}
          >
            作成
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>
          <Label>タスク名</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div>
          <Label>説明 (markdown)</Label>
          <Textarea value={desc} onChange={(e) => setDesc(e.target.value)} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>カテゴリ</Label>
            <Input
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder="抽出 / 変換 / 投入 / 検証"
            />
          </div>
          <div>
            <Label>実行方式</Label>
            <select
              className="input"
              value={type}
              onChange={(e) => setType(e.target.value as ExecutionType)}
            >
              <option value="manual">manual</option>
              <option value="automated">automated</option>
              <option value="hybrid">hybrid</option>
            </select>
          </div>
          <div>
            <Label>見積(分)</Label>
            <Input
              type="number"
              value={mins}
              min={0}
              onChange={(e) => setMins(Number(e.target.value) || 0)}
            />
          </div>
        </div>
        <div>
          <Label>依存タスク</Label>
          <select
            multiple
            className="input min-h-[6rem]"
            value={deps}
            onChange={(e) =>
              setDeps(Array.from(e.target.selectedOptions, (o) => o.value))
            }
          >
            {tasks.map((t) => (
              <option key={t.taskId} value={t.taskId}>
                {t.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    </Dialog>
  );
}

/**
 * Mobile fallback for the DAG canvas. ReactFlow's pan / zoom UX
 * collapses on phones (the bars are illegible and the touch gestures
 * fight the page's vertical scroll), so below `sm` we replace the
 * canvas with a vertical list that surfaces the same per-task signals
 * — name + status + execution kind + assignee + backlog count — and
 * lets the user tap into the detail panel just like the desktop card
 * click. A header banner explains why the chart is hidden so users
 * don't think the data is missing.
 */
function DagMobileFallback({
  tasks,
  executionByTaskId,
  assignees,
  backlogKeys,
  onSelectTask,
}: {
  tasks: readonly TaskDoc[];
  executionByTaskId: ReadonlyMap<string, ExecutionDoc>;
  assignees: readonly import('@mct/shared').AssigneeDoc[];
  backlogKeys: ReadonlySet<string>;
  onSelectTask: (taskId: string) => void;
}) {
  const assigneeById = useMemo(() => {
    const m = new Map<string, import('@mct/shared').AssigneeDoc>();
    for (const a of assignees) m.set(a.assigneeId, a);
    return m;
  }, [assignees]);
  return (
    <div
      data-testid="dag-mobile-fallback"
      className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-slate-50 px-3 pb-6 pt-3 sm:hidden"
    >
      <div className="mb-3 flex items-start gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
        <Monitor className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
        <div>
          <div className="font-medium text-slate-700">
            DAG チャートはデスクトップ表示専用です
          </div>
          <div className="mt-0.5 text-[11px] text-slate-500">
            画面が狭いため、タスク一覧で代替しています。タップで詳細を開けます。
          </div>
        </div>
      </div>
      {tasks.length === 0 ? (
        <div className="mt-6 rounded-md border border-dashed border-slate-300 bg-white px-3 py-6 text-center text-xs text-slate-500">
          タスクがまだ登録されていません。
        </div>
      ) : (
        <ul className="space-y-2">
          {tasks.map((t) => {
            const exec = executionByTaskId.get(t.taskId);
            const status = exec?.status ?? 'not_started';
            const completedAt = exec?.completedAt?.toDate?.() ?? null;
            const backlogCount = exec
              ? exec.backlogIssueKeys.filter((k) => backlogKeys.has(k)).length
              : 0;
            const assigneeDoc = t.assigneeId
              ? assigneeById.get(t.assigneeId) ?? null
              : null;
            return (
              <li key={t.taskId}>
                <button
                  type="button"
                  data-testid="dag-mobile-task-row"
                  onClick={() => onSelectTask(t.taskId)}
                  className="flex w-full flex-col items-stretch gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-left shadow-sm transition-colors hover:border-slate-300 hover:bg-slate-50"
                >
                  <div className="flex items-start gap-2">
                    <span className="line-clamp-2 min-w-0 flex-1 break-words text-sm font-semibold leading-snug text-slate-900">
                      {t.name}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-600">
                    <ExecutionKindChip value={t.executionType} />
                    {assigneeDoc ? (
                      <AssigneeChip
                        assignee={{
                          name: assigneeDoc.name,
                          colorIndex: assigneeDoc.colorIndex,
                        }}
                      />
                    ) : null}
                    {t.category ? (
                      <span className="rounded bg-slate-100 px-1">
                        {t.category}
                      </span>
                    ) : null}
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-slate-500">
                    <StatusBadge status={status} />
                    <div className="flex items-center gap-2">
                      {completedAt ? (
                        <span className="tabular-nums">
                          ✓ {completedAt.getMonth() + 1}/{completedAt.getDate()}
                        </span>
                      ) : null}
                      {backlogCount > 0 ? (
                        <span className="rounded bg-blue-50 px-1 text-blue-700">
                          Backlog:{backlogCount}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
